import React, { useMemo } from 'react';
import { gql } from '@apollo/client';
import { compact } from '@moda/portal-stanchions';
import { useBreakpoint } from '@moda/om';
import loadable from '@loadable/component';
import { VariantCellImageGalleryFragment } from '../../../../../generated/types';
import { getActiveVariantBy } from '../../../../../lib/getActiveVariantBy';
import { useGridCellImageSize } from '../../useGridCellImageSize';

const TUTOTRIAL_ENABLED = true;

export const VARIANT_CELL_IMAGE_GALLERY_VARIANT_FRAGMENT = gql`
  fragment VariantCellImageGalleryVariantFragment on VariantData {
    id
    primaryImageUrls: primary_image_urls {
      src: medium
      angle: key
    }
    alternateImageUrls: alternate_image_urls {
      src: medium
      angle: key
    }
  }
`;

export const VARIANT_CELL_IMAGE_GALLERY_FRAGMENT = gql`
  fragment VariantCellImageGalleryFragment on Variant {
    id
    name
    masterVariant: master_variants_data {
      id
      ...VariantCellImageGalleryVariantFragment
    }
    otherVariants: other_variants_data {
      id
      ...VariantCellImageGalleryVariantFragment
    }
  }
  ${VARIANT_CELL_IMAGE_GALLERY_VARIANT_FRAGMENT}
`;

const SwipeableImageGallery = loadable(() => import('../../../../SwipeableImageGallery'), {
  resolveComponent: exports => exports.SwipeableImageGallery
});

const ImageGallery = loadable(() => import('../../../../ImageGallery'), {
  resolveComponent: exports => exports.ImageGallery
});

interface Props {
  variant: VariantCellImageGalleryFragment;
  activeVariantId: string;
  active?: boolean;
  isFirstListItem?: boolean;
  onAngleChange: (angle: undefined | string) => void;
  currentAngle?: string;
}

export const VariantCellImageGallery: React.FC<Props> = ({
  variant,
  activeVariantId,
  active = false,
  isFirstListItem = false,
  onAngleChange,
  currentAngle
}) => {
  const { matches: isDesktop } = useBreakpoint({ gt: 'sm' });

  const { maxHeight, maxWidth } = useGridCellImageSize();

  const activeVariant = useMemo(
    () => getActiveVariantBy(variant, 'id', activeVariantId),
    [activeVariantId, variant]
  );

  const srcs = useMemo(
    () =>
      compact(
        compact([activeVariant.primaryImageUrls, ...activeVariant.alternateImageUrls]).map(
          ({ src, angle }) => ({ src, angle })
        )
      ),
    [activeVariant.alternateImageUrls, activeVariant.primaryImageUrls]
  );

  const images = useMemo(
    () => srcs.map(({ src, angle }) => ({ src, title: variant.name, angle: angle || undefined })),
    [srcs, variant.name]
  );

  // If currentAngle exists, use it first
  const orderedImages = useMemo(() => {
    if (!currentAngle) return images;

    const idx = images.findIndex(img => img.angle === currentAngle);
    if (idx <= 0) return images;
    return [images[idx], ...images.slice(0, idx), ...images.slice(idx + 1)];
  }, [images, currentAngle]);

  if (isDesktop) {
    return (
      <ImageGallery
        images={orderedImages}
        active={active}
        maxHeight={maxHeight}
        maxWidth={maxWidth}
      />
    );
  }

  return (
    <SwipeableImageGallery
      images={orderedImages}
      active={active}
      maxHeight={maxHeight}
      maxWidth={maxWidth}
      enableTutorial={TUTOTRIAL_ENABLED && isFirstListItem}
      onAngleChange={onAngleChange}
      currentAngle={currentAngle}
    />
  );
};
